import React, { useState, useEffect } from 'react';
import { Brain, Database, Send, ArrowRight, Code, MessageCircle, Zap, Clock, CheckCircle } from 'lucide-react';
import FieldAwareTextArea from '../../components/FieldAwareTextArea';

const AdminFieldIntegrationDemo = () => {
    const [configurations, setConfigurations] = useState([]);
    const [selectedConfigId, setSelectedConfigId] = useState('');
    const [activeExample, setActiveExample] = useState('customers');
    const [naturalQuery, setNaturalQuery] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [showSqlResult, setShowSqlResult] = useState(false);
    const [generatedSql, setGeneratedSql] = useState('');
    const [sqlError, setSqlError] = useState('');
    const [queryResults, setQueryResults] = useState(null);
    const [isExecuting, setIsExecuting] = useState(false);

    // Natural language query examples
    const queryExamples = {
        customers: {
            title: "Customer Queries",
            icon: "👤",
            examples: [
                {
                    natural: "Show me all customers who bought cars in the last 30 days",
                    sql: `SELECT c.first_name, c.last_name, c.email, o.purchase_date, v.make, v.model
FROM customers c
JOIN orders o ON c.id = o.customer_id
JOIN vehicles v ON o.vehicle_id = v.id
WHERE o.purchase_date >= CURRENT_DATE - INTERVAL '30 days'
ORDER BY o.purchase_date DESC;`,
                    description: "Retrieves recent car buyers with vehicle details"
                },
                {
                    natural: "Find customers from London who need MOT reminders",
                    sql: `SELECT c.first_name, c.last_name, c.phone, c.email, v.make, v.model,
       v.registration_number, s.last_mot_date, s.next_mot_due
FROM customers c
JOIN vehicles v ON c.id = v.owner_id
JOIN service_records s ON v.id = s.vehicle_id
WHERE c.city = 'London'
  AND s.next_mot_due <= CURRENT_DATE + INTERVAL '30 days'
ORDER BY s.next_mot_due ASC;`,
                    description: "Identifies London customers due for upcoming MOT tests"
                }
            ]
        },
        sales: {
            title: "Sales Analytics",
            icon: "📊",
            examples: [
                {
                    natural: "What are our top selling car models this year?",
                    sql: `SELECT v.make, v.model, COUNT(*) as sales_count,
       AVG(o.sale_price) as avg_price_gbp, SUM(o.sale_price) as total_revenue_gbp
FROM vehicles v
JOIN orders o ON v.id = o.vehicle_id
WHERE EXTRACT(YEAR FROM o.purchase_date) = EXTRACT(YEAR FROM CURRENT_DATE)
GROUP BY v.make, v.model
ORDER BY sales_count DESC
LIMIT 10;`,
                    description: "Analyses vehicle sales performance by model in GBP"
                },
                {
                    natural: "Show me sales by dealership location for this quarter",
                    sql: `SELECT dl.name as dealer_name, dl.city, dl.county, dl.postcode,
       COUNT(o.id) as total_sales,
       SUM(o.sale_price) as revenue_gbp,
       AVG(o.sale_price) as avg_sale_price_gbp
FROM dealer_locations dl
JOIN orders o ON dl.id = o.dealer_location_id
WHERE o.purchase_date >= DATE_TRUNC('quarter', CURRENT_DATE)
GROUP BY dl.id, dl.name, dl.city, dl.county, dl.postcode
ORDER BY revenue_gbp DESC;`,
                    description: "Quarterly sales performance by UK dealership"
                }
            ]
        },
        service: {
            title: "Service Operations",
            icon: "🔧",
            examples: [
                {
                    natural: "Which customers have overdue MOT or service appointments?",
                    sql: `SELECT c.first_name, c.last_name, c.phone, c.email,
       v.make, v.model, v.year, v.registration_number,
       sr.last_service_date, sr.next_service_due,
       sr.last_mot_date, sr.next_mot_due,
       CURRENT_DATE - LEAST(sr.next_service_due, sr.next_mot_due) as days_overdue
FROM customers c
JOIN vehicles v ON c.id = v.owner_id
JOIN service_records sr ON v.id = sr.vehicle_id
WHERE sr.next_service_due < CURRENT_DATE OR sr.next_mot_due < CURRENT_DATE
ORDER BY days_overdue DESC;`,
                    description: "Lists customers with overdue MOT or service appointments"
                },
                {
                    natural: "Find all Jaguar vehicles that need services this month",
                    sql: `SELECT v.make, v.model, v.year, v.registration_number,
       c.first_name, c.last_name, c.phone,
       sr.last_service_date, sr.next_service_due, sr.service_type
FROM vehicles v
JOIN customers c ON v.owner_id = c.id
JOIN service_records sr ON v.id = sr.vehicle_id
WHERE v.make = 'Jaguar'
  AND sr.next_service_due <= LAST_DAY(CURRENT_DATE)
  AND sr.next_service_due >= DATE_TRUNC('month', CURRENT_DATE)
ORDER BY sr.next_service_due ASC;`,
                    description: "Jaguar vehicles requiring services this month"
                }
            ]
        }
    };

    useEffect(() => {
        loadConfigurations();
    }, []);

    const loadConfigurations = async () => {
        try {
            const response = await fetch('/api/admin/database/configurations');
            const result = await response.json();

            if (response.ok) {
                setConfigurations(result.configurations || []);
                if (result.configurations && result.configurations.length > 0) {
                    setSelectedConfigId(result.configurations[0].id);
                }
            }
        } catch (error) {
            console.error('Failed to load configurations:', error);
        }
    };

    const processNaturalLanguageQuery = async (query) => {
        setIsProcessing(true);
        setShowSqlResult(false);
        setGeneratedSql('');
        setSqlError('');
        setQueryResults(null);

        try {
            const response = await fetch('/api/natural-language/convert', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: query,
                    configId: selectedConfigId
                })
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setGeneratedSql(result.sql);
                setShowSqlResult(true);
            } else {
                setSqlError(result.error || 'Failed to generate SQL');
                setShowSqlResult(true);
            }
        } catch (error) {
            console.error('Error processing natural language query:', error);
            setSqlError('Network error: Could not connect to the AI service');
            setShowSqlResult(true);
        } finally {
            setIsProcessing(false);
        }
    };

    const executeGeneratedSql = async () => {
        if (!generatedSql || !selectedConfigId) return;

        setIsExecuting(true);
        setQueryResults(null);

        try {
            const response = await fetch('/api/natural-language/execute', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sql: generatedSql,
                    configId: selectedConfigId
                })
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setQueryResults(result);
            } else {
                setSqlError(result.error || 'Failed to execute SQL');
            }
        } catch (error) {
            console.error('Error executing SQL:', error);
            setSqlError('Network error: Could not execute query');
        } finally {
            setIsExecuting(false);
        }
    };

    const loadExampleQuery = (example) => {
        setNaturalQuery(example.natural);
        setShowSqlResult(false);
    };

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Brain size={24} />
                    Natural Language Query Builder (Preview)
                </h1>
                <p className="text-gray-600">
                    Convert natural language questions into SQL queries using AWS Bedrock AI. Write queries in plain English and execute them on your databases.
                </p>
                <div className="mt-3 flex items-center gap-2 text-sm">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✅ Live
                    </span>
                    <span className="text-gray-500">Powered by AWS Bedrock AI</span>
                </div>
            </div>

            {/* Configuration Selection */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Database size={20} />
                    Database Configuration
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Database Configuration
                        </label>
                        <select
                            value={selectedConfigId}
                            onChange={(e) => setSelectedConfigId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Select a configuration...</option>
                            {configurations.map((config) => (
                                <option key={config.id} value={config.id}>
                                    {config.name} ({config.type})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <div className="text-sm text-gray-600">
                            {selectedConfigId ? (
                                <div className="flex items-center gap-2 text-green-600">
                                    <CheckCircle size={16} />
                                    AI Query Builder Ready
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 text-yellow-600">
                                    <Clock size={16} />
                                    Select configuration to enable AI queries
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Natural Language Query Builder */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <Brain size={20} className="text-purple-600" />
                            Natural Language Query Builder
                        </h2>
                        <p className="text-sm text-gray-600">
                            Ask questions in plain English and get SQL queries automatically generated.
                        </p>
                    </div>

                    <div className="p-6">
                        {/* Query Input */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Ask a question about your data
                            </label>
                            <textarea
                                value={naturalQuery}
                                onChange={(e) => setNaturalQuery(e.target.value)}
                                placeholder="e.g., Show me all customers who bought cars in the last 30 days"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                            />
                        </div>

                        {/* Process Button */}
                        <div className="mb-6">
                            <button
                                onClick={() => processNaturalLanguageQuery(naturalQuery)}
                                disabled={!naturalQuery.trim() || !selectedConfigId || isProcessing}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {isProcessing ? (
                                    <>
                                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                                        Processing with AI...
                                    </>
                                ) : (
                                    <>
                                        <Zap size={16} />
                                        Generate SQL
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Processing Animation */}
                        {isProcessing && (
                            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                                <div className="flex items-center gap-3 text-purple-700">
                                    <div className="animate-pulse flex gap-1">
                                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                    </div>
                                    <span className="text-sm font-medium">AWS Bedrock is analyzing your query and database schema...</span>
                                </div>
                            </div>
                        )}

                        {/* Generated SQL or Error */}
                        {showSqlResult && (
                            <div className="mb-6">
                                {sqlError ? (
                                    <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-red-600 text-sm font-medium">Error</span>
                                        </div>
                                        <p className="text-red-700 text-sm">{sqlError}</p>
                                    </div>
                                ) : generatedSql ? (
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Code size={16} className="text-green-600" />
                                            <span className="text-sm font-medium text-gray-700">Generated SQL Query</span>
                                        </div>
                                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                            <pre>{generatedSql}</pre>
                                        </div>
                                        <div className="mt-3 flex gap-2">
                                            <button
                                                onClick={executeGeneratedSql}
                                                disabled={isExecuting}
                                                className="flex items-center gap-2 px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                            >
                                                {isExecuting ? (
                                                    <>
                                                        <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
                                                        Executing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send size={14} />
                                                        Execute Query
                                                    </>
                                                )}
                                            </button>
                                            <button className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                                                <MessageCircle size={14} />
                                                Use in Template
                                            </button>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {/* Query Results */}
                        {queryResults && (
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <CheckCircle size={16} className="text-green-600" />
                                    <span className="text-sm font-medium text-gray-700">Query Results</span>
                                    <span className="text-xs text-gray-500">({queryResults.rowCount} rows)</span>
                                </div>
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="bg-gray-50 px-4 py-2 border-b">
                                        <span className="text-xs text-gray-600 font-medium">Sample Results (first 10 rows)</span>
                                    </div>
                                    <div className="overflow-x-auto max-h-64">
                                        {queryResults.data && queryResults.data.length > 0 ? (
                                            <table className="w-full text-xs">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        {Object.keys(queryResults.data[0]).map((column, index) => (
                                                            <th key={index} className="px-3 py-2 text-left font-medium text-gray-700">
                                                                {column}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {queryResults.data.slice(0, 10).map((row, index) => (
                                                        <tr key={index} className="border-t">
                                                            {Object.values(row).map((value, colIndex) => (
                                                                <td key={colIndex} className="px-3 py-2 text-gray-600">
                                                                    {value !== null ? String(value) : <span className="text-gray-400">NULL</span>}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div className="p-4 text-center text-gray-500">
                                                No data returned
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Query Examples */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Example Queries</h2>
                        <p className="text-sm text-gray-600">
                            Try these example natural language queries to see how they convert to SQL.
                        </p>
                    </div>

                    <div className="p-6">
                        {/* Category Selector */}
                        <div className="mb-4">
                            <div className="flex gap-2 mb-4">
                                {Object.entries(queryExamples).map(([key, category]) => (
                                    <button
                                        key={key}
                                        onClick={() => setActiveExample(key)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${
                                            activeExample === key
                                                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        <span>{category.icon}</span>
                                        {category.title}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Examples List */}
                        <div className="space-y-4">
                            {queryExamples[activeExample].examples.map((example, index) => (
                                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                                    <div className="mb-2">
                                        <div className="flex items-start gap-2 mb-2">
                                            <MessageCircle size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-900">{example.natural}</p>
                                                <p className="text-xs text-gray-500 mt-1">{example.description}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 mt-3">
                                            <ArrowRight size={14} className="text-gray-400" />
                                            <span className="text-xs text-gray-500">Generates SQL:</span>
                                        </div>
                                        <div className="mt-2 bg-gray-900 text-green-400 p-3 rounded text-xs font-mono">
                                            <pre className="whitespace-pre-wrap">{example.sql.split('\n').slice(0, 3).join('\n')}...</pre>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => loadExampleQuery(example)}
                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        Try this query →
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default AdminFieldIntegrationDemo;